import React from 'react';
import { Col, Divider, Row } from 'antd';
import { isEqual } from 'lodash';

import {
  CustomerHistoryDetailsModel,
  FullCustomerInfoResponseModel,
  ServiceAgreementTypeEnum,
} from 'api/adminApiClient';
import { useAppDispatch } from 'hooks/useAppDispatch';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { getServiceAgreementFileInfoForCustomer } from 'redux/reducers/clientSlice';
import { downloadFile } from 'utils/fileUtils';
import HistoryLog from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/HistoryLog';
import HistoryField from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/HistoryField';
import { getAgreementTypeLabel } from 'utils/clientUtils';

import styles from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/HistoryCard.module.less';

type Props = {
  customerId: string;
  historyDetails: CustomerHistoryDetailsModel;
  historyId: string;
};

const resolveAgreementDetailsInfo = (
  customerDetails?: FullCustomerInfoResponseModel,
) => customerDetails?.generalInfo?.agreementDetailsInfo;

const mapAttachmentFileKeys = (
  customerDetails?: FullCustomerInfoResponseModel,
) =>
  (resolveAgreementDetailsInfo(customerDetails)?.agreementAttachments ?? []).map(
    (file) => file.fileKey,
  );

export default function AgreementDetailsCard(props: Props) {
  const { historyDetails, customerId, historyId } = props;
  const { oldData, newData } = historyDetails;
  const dispatch = useAppDispatch();

  const oldAgreementInfo = resolveAgreementDetailsInfo(oldData);
  const newAgreementInfo = resolveAgreementDetailsInfo(newData);

  const hasAgreementAttachmentsChanged = !isEqual(
    mapAttachmentFileKeys(oldData),
    mapAttachmentFileKeys(newData),
  );
  const hasContractSignerFirstNameChanged = !isEqual(
    oldAgreementInfo?.contractSignerFirstName,
    newAgreementInfo?.contractSignerFirstName,
  );
  const hasContractSignerLastNameChanged = !isEqual(
    oldAgreementInfo?.contractSignerLastName,
    newAgreementInfo?.contractSignerLastName,
  );
  const hasContractSignerPhoneChanged = !isEqual(
    oldAgreementInfo?.contractSignerPhone,
    newAgreementInfo?.contractSignerPhone,
  );
  const hasContractSignerEmailChanged = !isEqual(
    oldAgreementInfo?.contractSignerEmail,
    newAgreementInfo?.contractSignerEmail,
  );

  const hasContractSignerChanged =
    hasContractSignerFirstNameChanged ||
    hasContractSignerLastNameChanged ||
    hasContractSignerPhoneChanged ||
    hasContractSignerEmailChanged;

  const hasAnyDisplayableChange =
    hasAgreementAttachmentsChanged || hasContractSignerChanged;

  const handleAgreementDetailsFileDownload = async (
    fileKey?: string,
    fileName?: string,
  ) => {
    const result = await dispatch(
      getServiceAgreementFileInfoForCustomer({
        customerId,
        fileKey,
        fileName,
        isAdminRole: true,
      }),
    );

    if (getServiceAgreementFileInfoForCustomer.fulfilled.match(result)) {
      try {
        await downloadFile({
          downloadUrl: result.payload.downloadUrl,
          fileName: result.payload.fileName,
        });
      } catch (e: any) {
        BaseAlert.error(e.message);
      }
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const renderAgreementDetails = (
    customerDetails?: FullCustomerInfoResponseModel,
  ) => {
    const agreementDetailsInfo = resolveAgreementDetailsInfo(customerDetails);
    const agreementDetailsType = agreementDetailsInfo?.agreementDetailsType;
    const agreementAttachments = agreementDetailsInfo?.agreementAttachments ?? [];
    const agreementTypeLabel = getAgreementTypeLabel(agreementDetailsType);
    const sectionName = 'agreement-details';

    return (
      <>
        {hasAgreementAttachmentsChanged && (
          <Row gutter={[10, 10]}>
            <Col xs={24}>
              <div className={styles.cardTitle}>Agreement details</div>
            </Col>

            {agreementDetailsType === ServiceAgreementTypeEnum.MemberAgreement ||
            agreementDetailsType === ServiceAgreementTypeEnum.ClientAgreement ||
            agreementDetailsType ===
              ServiceAgreementTypeEnum.MyBasePayAgreement ? (
              <HistoryField
                title={agreementTypeLabel}
                emptyText="No files"
                value={
                  agreementAttachments.length > 0
                    ? agreementAttachments.map((file) => (
                        <div
                          className="link-imitation"
                          key={file.fileKey}
                          onClick={() =>
                            handleAgreementDetailsFileDownload(
                              file.fileKey,
                              file.fileName,
                            )
                          }
                        >
                          {file?.fileName}
                        </div>
                      ))
                    : null
                }
                dataId={`${sectionName}-file-field`}
              />
            ) : (
              <Col xs={24} md={12}>
                <div className="title">{agreementTypeLabel}</div>
              </Col>
            )}
          </Row>
        )}

        {hasAgreementAttachmentsChanged && hasContractSignerChanged && (
          <Divider />
        )}

        {hasContractSignerChanged && (
          <Row gutter={[10, 10]}>
            <Col xs={24}>
              <div className={styles.cardTitle}>Customer Contract Signer</div>
            </Col>

            {hasContractSignerFirstNameChanged && (
              <HistoryField
                title="First name"
                value={agreementDetailsInfo?.contractSignerFirstName}
                dataId={`${sectionName}-contract-signer-first-name-field`}
              />
            )}

            {hasContractSignerLastNameChanged && (
              <HistoryField
                title="Last name"
                value={agreementDetailsInfo?.contractSignerLastName}
                dataId={`${sectionName}-contract-signer-last-name-field`}
              />
            )}

            {hasContractSignerPhoneChanged && (
              <HistoryField
                title="Phone"
                value={agreementDetailsInfo?.contractSignerPhone}
                dataId={`${sectionName}-contract-signer-phone-field`}
              />
            )}

            {hasContractSignerEmailChanged && (
              <HistoryField
                title="Email"
                value={agreementDetailsInfo?.contractSignerEmail}
                dataId={`${sectionName}-contract-signer-email-field`}
              />
            )}
          </Row>
        )}

        {(hasAgreementAttachmentsChanged || hasContractSignerChanged) && (
          <Divider />
        )}
      </>
    );
  };

  if (!hasAnyDisplayableChange) {
    return null;
  }

  return (
    <HistoryLog
      historyId={historyId}
      oldData={renderAgreementDetails(oldData)}
      newData={renderAgreementDetails(newData)}
    />
  );
}
